import { noop } from 'lodash/fp';
import { MutableRefObject, ReactNode, useCallback, useEffect, useRef } from 'react';
import { SwipeableHandlers, useSwipeable } from 'react-swipeable';
import { CSSTransition } from 'react-transition-group';
import { BORDER_RADIUSES, BOX_SHADOWS, MEDIA_QUERIES } from 'shared/styles/variables';
import styled from 'styled-components';

import FocusTrap from 'inquiry/components/FocusTrap';
import { useEscListener } from 'lib/hooks/useKeyListener';
import useToggle from 'lib/hooks/useToggle';
import { colorCss, sizeCss } from 'lib/inquiryStyles';
import { SerializedBaseStepStyles } from 'lib/interfaces/StepsConfig';
import { FRAME_HEIGHT, FRAME_MARGIN_Y } from 'verify/components/Application/Frame';
import { focusTitleElement } from 'verify/lib/a11y';
import { Z_INDICES } from 'verify/styles/variables';

const SLIDE_DURATION = 250;

const Backdrop = styled.div`
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: ${Z_INDICES.drawer};

  @media ${MEDIA_QUERIES.isMobile} {
    position: fixed;
  }

  &.dialog-enter {
    background-color: rgba(0, 0, 0, 0);
    pointer-events: none;
  }

  &.dialog-enter-active,
  &.dialog-enter-done {
    background-color: rgba(0, 0, 0, 0.7);
    transition: background-color ${SLIDE_DURATION}ms;
    pointer-events: unset;
  }

  &.dialog-exit {
    background-color: rgba(0, 0, 0, 0.7);
    pointer-events: unset;
  }
  &.dialog-exit-active {
    background-color: rgba(0, 0, 0, 0);
    transition: background-color ${SLIDE_DURATION}ms;
    pointer-events: none;
  }
`;

const Anim = styled.div`
  position: absolute;
  bottom: 0;
  width: 100%;
  transition: ${SLIDE_DURATION}ms;

  .dialog-enter & {
    transform: translateY(100%);
  }
  .dialog-enter-active & {
    transform: translateY(0%);
  }

  .dialog-exit & {
    transform: translateY(0%);
  }
  .dialog-exit-active & {
    transform: translateY(100%);
  }
`;

const DialogContainer = styled.div<{ swipeable: boolean; styles?: DialogStyles }>`
  box-shadow: 0 0 6px 0 ${BOX_SHADOWS.base};

  background-color: ${({ styles, theme }) =>
    colorCss(styles?.backgroundColor) ?? theme.colorModalBackground};
  color: ${({ theme }) => theme.colorFont};
  margin: 4px;
  pointer-events: all;
  user-select: none;
  position: relative;

  ${({ swipeable }) =>
    swipeable &&
    `
    cursor: grab;

    &:active {
      cursor: grabbing;
    }
  `}

  ${({ styles }) => `
    border-radius: ${sizeCss(styles?.border?.radius) ?? BORDER_RADIUSES.rounded};
    padding-top: calc(${sizeCss(styles?.padding?.top) ?? '16px'} - ${INNER_PADDING}px);
    padding-bottom: calc(${sizeCss(styles?.padding?.bottom) ?? '16px'} - ${INNER_PADDING}px);
    padding-left: calc(${sizeCss(styles?.padding?.left) ?? '24px'} - ${INNER_PADDING}px);
    padding-right: calc(${sizeCss(styles?.padding?.right) ?? '24px'} - ${INNER_PADDING}px);
  `}

  // leave 40px of space at the top so its
  // possible to click on the backdrop to dismiss the modal
  @media ${MEDIA_QUERIES.isNotMobile} {
    max-height: ${FRAME_HEIGHT - 40}px;
  }

  @media ${MEDIA_QUERIES.isMobile} {
    max-height: calc(100vh - 40px);
  }

  // flex is necessary to restrict height of children and make scrollable
  display: flex;
  flex-direction: column;
`;

const SwipeHandle = styled.div`
  position: absolute;
  top: 8px;
  left: 0;
  right: 0;
`;
const SwipeHandleInner = styled.div`
  width: 60px;
  margin: auto;
  height: 4px;
  border-radius: ${BORDER_RADIUSES.rounded};
  background-color: ${({ theme }) => theme.colorDivider};
`;

interface DialogProps {
  isOpen: boolean;
  /**
   * If not passed, cannot swipe down to dismiss. Dialog must contain actions
   * that close the dialog.
   */
  onClose?: () => void;
  children: ReactNode;
  styles?: DialogStyles;
  /**
   * Required for a11y.
   */
  'aria-labelledby': string;
  /**
   * Required for a11y.
   */
  'aria-describedby': string;
  'data-test'?: string;
}

/* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
type DialogStyles = SerializedBaseStepStyles['base']['modal'];

function Dialog({
  children,
  isOpen,
  onClose,
  styles,
  'aria-labelledby': ariaLabelledBy,
  'aria-describedby': ariaDescribedBy,
  'data-test': dataTest = 'dialog',
}: DialogProps) {
  // Because the dialog is animated, we also track animation state to determine if the UI is ready for interaction.
  const [visible, { enable: setVisible, disable: setNotVisible }] = useToggle();
  // onClose is optional, but useEscListener errors if onClose is null.
  useEscListener(onClose ?? noop);

  const swipeHandlers = useSwipeable({
    onSwipedDown: () => onClose?.(),
    preventDefaultTouchmoveEvent: true,
    trackTouch: true,
    // needed to enable on desktop
    trackMouse: true,
  });

  const swipeable = onClose != null;

  const dialogInnerRef = useRef<HTMLElement | null>(null);

  const handleOpenAnimationEnd = useCallback(() => {
    setVisible();
    focusTitleElement(dialogInnerRef.current);
  }, []);

  const handleCloseAnimationEnd = useCallback(() => {
    // Return focus to current page heading
    setTimeout(focusTitleElement, 0);
  }, []);

  return (
    <CSSTransition
      in={isOpen}
      timeout={{
        enter: SLIDE_DURATION,
        exit: SLIDE_DURATION,
      }}
      mountOnEnter={true}
      unmountOnExit={true}
      onEntered={handleOpenAnimationEnd}
      onExit={setNotVisible}
      onExited={handleCloseAnimationEnd}
      classNames='dialog'
    >
      <FocusTrap
        // Use active instead of disabled because this is expected to be toggled.
        active={visible}
        focusTrapOptions={{
          // Block ALL outside interaction.
          escapeDeactivates: false,
          clickOutsideDeactivates: false,
          allowOutsideClick: false,
          // We coordinate focus outside with refs, which is easier when animations are involved.
          initialFocus: false,
          // Needed to stop FocusTrap from erroring before content has rendered.
          fallbackFocus: document.body,
          setReturnFocus: false,
        }}
      >
        <Backdrop data-test='dialog__backdrop' id='dialog__backdrop' onClick={onClose}>
          <Anim id='dialog__animation' onClick={(e) => e.stopPropagation()}>
            <DialogInner
              styles={styles}
              dialogContainerRef={dialogInnerRef}
              data-test={dataTest}
              aria-labelledby={ariaLabelledBy}
              aria-describedby={ariaDescribedBy}
              swipeable={swipeable}
              swipeHandlers={swipeHandlers}
            >
              {swipeable && (
                <SwipeHandle role='presentation'>
                  <SwipeHandleInner />
                </SwipeHandle>
              )}
              <DialogScrollContainer>{children}</DialogScrollContainer>
            </DialogInner>
          </Anim>
        </Backdrop>
      </FocusTrap>
    </CSSTransition>
  );
}

export default Dialog;

interface DialogInnerProps
  extends Pick<DialogProps, 'styles' | 'data-test' | 'aria-labelledby' | 'aria-describedby'> {
  children: ReactNode;
  swipeable: boolean;
  swipeHandlers: SwipeableHandlers;
  dialogContainerRef: MutableRefObject<HTMLElement | null>;
}

const DialogInner = ({
  dialogContainerRef,
  swipeHandlers: { ref: swipeHandlerRef, ...swipeProps },
  ...props
}: DialogInnerProps) => {
  // While this measures layout, useLayoutEffect interferes with the animation.
  useEffect(() => {
    // scroll to dialog if it appears off screen
    if (dialogContainerRef.current != null && !isInViewport(dialogContainerRef.current)) {
      document.body.scrollTo?.({ top: window.innerHeight - FRAME_MARGIN_Y, behavior: 'smooth' });
    }
  }, []);

  return (
    <DialogContainer
      role='dialog'
      {...swipeProps}
      {...props}
      ref={(element) => {
        dialogContainerRef.current = element;
        swipeHandlerRef(element);
      }}
    />
  );
};

// Prevent button focus ring from getting clipped
const INNER_PADDING = 4;

const DialogScrollContainer = styled.div`
  overflow-y: auto;
  padding: ${INNER_PADDING}px;
`;

function isInViewport(element: HTMLElement) {
  const { top, height } = element.getBoundingClientRect();

  // Gives the position of the center of the dialog. Note that the dialog
  // starts off screen so "top" is really the bottom. We use the center as a
  // heuristic for what's "obviously visible" to the user and only scroll if
  // the center is off screen.
  const center = top - height / 2;

  return center < window.innerHeight;
}
